import { createClient } from "@connectrpc/connect";
import { grpcTransportProvider } from "../grpc/GrpcTransport";
import { mapConnectError } from "../grpc/mapConnectError";
import {
  LoginPlataformaResponse as LoginPlataformaGrpcResponse,
  LoginPlataformaService,
} from "../../generated/grpc/login_plataforma_pb";
import { LoginPlataformaResponse } from "../../response/seguridad/LoginPlataformaResponse";

/** Login de Plataforma (`LoginPlataformaService.LoginPlataforma`/`CheckTokenPlataforma`,
 *  gRPC-Web). Logica propia -no reusa LoginPlataformaService (REST)-, mismo
 *  criterio que el login de Cliente (ver CLAUDE.md del Server, seccion
 *  migracion a gRPC-Web). */
class LoginPlataformaGrpcService {
  /** Inicio de sesión con email y contraseña. */
  async loginAsync(email: string, password: string): Promise<LoginPlataformaResponse> {
    const client = createClient(LoginPlataformaService, grpcTransportProvider.get());
    try {
      const response = await client.loginPlataforma({ email, password });
      return toResponse(response);
    } catch (error) {
      throw mapConnectError(error);
    }
  }

  /** Reanuda la sesión guardada; si ya no vale, el servidor responde `NotFound`. */
  async checkTokenAsync(token: string): Promise<LoginPlataformaResponse> {
    const client = createClient(LoginPlataformaService, grpcTransportProvider.get());
    try {
      const response = await client.checkTokenPlataforma({ token });
      return toResponse(response);
    } catch (error) {
      throw mapConnectError(error);
    }
  }

  /** SSO desde el portal de Nitria (`LoginPortalPlataforma`, solo Nitrofert,
   *  decidido 2026-09-30): inicia sesión con el token del portal. Cada rechazo
   *  del servidor llega como `ValidationError` con el motivo exacto. */
  async loginPortalAsync(token: string): Promise<LoginPlataformaResponse> {
    const client = createClient(LoginPlataformaService, grpcTransportProvider.get());
    try {
      const response = await client.loginPortalPlataforma({ token });
      return toResponse(response);
    } catch (error) {
      throw mapConnectError(error);
    }
  }
}

/** Adapta el mensaje gRPC al shape que ya consume el resto de la app (mismo
 *  que devolvía REST). Cada `has*` ya incluye al super usuario. */
function toResponse(response: LoginPlataformaGrpcResponse): LoginPlataformaResponse {
  return {
    token: response.token,
    nombre: response.nombre,
    hasSuperAdmin: response.hasSuperAdmin,
    hasComercial: response.hasComercial,
    hasHardware: response.hasHardware,
    hasPilotos: response.hasPilotos,
    hasConductores: response.hasConductores,
    hasOficina: response.hasOficina,
    tecnicoId: response.tecnicoId ?? null,
    promotorId: response.promotorId ?? null,
  };
}

/** Singleton del servicio de login de Plataforma, variante gRPC (sin estado). */
export const loginPlataformaGrpcService = new LoginPlataformaGrpcService();
